// Modo "Mostrar cómo funciona" de BELLEZA Y CUIDADO PERSONAL — pantalla propia
// (ruta `/mostrar-como-funciona-belleza`).
//
// Video corto (5-8 s) del producto de la marca: una modelo usándolo, o el producto solo
// en escena. Parte de una IMAGEN —una creación del usuario o una foto que sube— y de tres
// decisiones: qué pasa, dónde pasa, y las indicaciones que quiera agregar.
// Ver ADR-2026-09-10-modo-mostrar-como-funciona-belleza.
//
// Sólo para marcas del rubro "Belleza y Cuidado Personal": `soloBelleza` en
// `modesCatalog` esconde la card en todas las listas, y esta pantalla además rebota a la
// home si alguien llega por URL con otra marca activa.
//
// DE DÓNDE SALE. Replica el modo homónimo de Hogar y Decoración (rama
// `nuevos-modos-hogar-y-deco`): las mismas cuatro piezas de la columna —la card de la
// imagen de partida, el bloque "Qué pasa en el video", la card "Dónde pasa" con su panel
// lateral, y la instrucción libre al pie—. Lo que NO se trajo y por qué está en el ADR:
// la tercera acción ("Se transforma", que no tiene sentido en un labial), el picker de
// Formato (el backend fija 9:16) y el marco dibujado a mano (acá lo pone
// `ShellModoRedisenado`, que es el mismo dibujo ya compartido).
//
// CON BACKEND, a diferencia del original: corre sobre el pipeline de "Animar imagen"
// (`/v1/videos/from-image`), con el prompt armado en `mostrarComoFuncionaBelleza.ts`, y
// lo generado aterriza en la columna de resultados —
// ADR-2026-09-10-mostrar-como-funciona-belleza-corre-sobre-animar-imagen.
//
// OJO con los colores: los tokens `brand-*` no declaran `<alpha-value>`, así que
// `bg-brand-primary/10` no emite regla (queda transparente). Para los azules con alpha va
// el hex arbitrario (`bg-[#1644EB]/10`) y para los grises, `text-black/<n>`.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type LucideIcon,
  Aperture,
  Bath,
  Hand,
  ImageUp,
  Leaf,
  Loader2,
  PencilLine,
  Sparkles,
  SprayCan,
  Sun,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import { useSidebar } from '../../../context/SidebarContext';
import { useVolverAtras } from '../../../hooks/useVolverAtras';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { esRubroBelleza } from '../../../lib/rubro';
import CachedImg from '../../../components/CachedImg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { CreditBadge } from '../../../components/CreditBadge';
import { isProOrUltra } from '../../../lib/planCapabilities';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  SUBIDA_REQUIERE_ATLAS,
  buildAnimarVideoBody,
  generateVideoFromImage,
  type AnimarSource,
} from '../../../lib/videoFromImage';
import { fetchUserModels, type ModelV2 } from '../modelsApi';
import { modelSelectionLabel, type ModelSelection } from '../ModelPicker';
import {
  ModelPickerInline,
  type ModelPickerView,
} from '../modelo/ModelPickerInline';
import { GeneradasPickGrid, type GeneradaPick } from './shared/GeneradasPickGrid';
import { PasoUnoMobile } from './shared/PasoUnoMobile';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import { CardPicker } from '../components/PanelCatalogoCompacto';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { CeldaAjuste } from '../components/AjustesRapidosProducto';
import { FillBarSliderV2 } from '../components/FillBarSliderV2';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import { nombreCortoModo } from '../modesCatalog';
import {
  ACCIONES_VIDEO,
  AMBIENTES,
  AMBIENTE_PROPIO,
  AMBIENTE_PROPIO_ID,
  DURACION_DEFAULT,
  DURACION_MAX,
  DURACION_MIN,
  accionTieneModelo,
  accionVideoPorId,
  ambientePorId,
  costoVideo,
  descripcionModeloEnPalabras,
  promptVideoBelleza,
  type AccionVideo,
  type AccionVideoId,
  type AmbienteElegido,
  type AmbienteId,
} from '../mostrarComoFuncionaBelleza';
import { scrollToAndShake, type Viewport } from '../scrollShake';
import { MAX_TANDAS_EN_CURSO } from '../generacionesEnCurso';
import { palabraDeModelo } from '../../../lib/nombreDeModelo';
import { leerSaldo } from '../../../lib/saldo';

/** Segmento de ruta del modo — el mismo `path` que declara `modesCatalog`. */
const PATH_MODO = 'mostrar-como-funciona-belleza';
/** El `kind` del preset: rotula las tandas en la columna de resultados. */
const KIND_MODO = 'mostrar-como-funciona-belleza';

/**
 * Carátula del modo. PLACEHOLDER: es el clip del lápiz labial del onboarding del rubro
 * (9:16, con su primer frame como poster). Falta un cuadro propio del modo.
 */
const HERO_VIDEO = '/images/onboarding/rubros/belleza/lapiz/video.mp4';
const HERO_POSTER = '/images/onboarding/rubros/belleza/lapiz/video-poster.jpg';

const HERO_TITULO = 'Tu producto en movimiento';
const HERO_DESC =
  'Elegí una foto, contá qué pasa y dónde: Atlas devuelve un video corto listo para publicar.';

/** Estimación para la barra de carga de mobile: una escena de Grok tarda 1-3 min. */
const CARGA_MS = 180_000;

/** Cuántas veces se pollea la fila del video antes de mandar al historial igual. */
const POLL_INTENTOS = 15;
const POLL_INTERVALO_MS = 3000;

/**
 * Ícono de cada acción, en el mismo trazo de `lucide` que el resto de la columna.
 *
 * `SprayCan` y no el `Sparkle` de antes: los dos tiles se distinguen por si hay o no
 * alguien en cuadro, y una chispa no dice nada de eso. Un envase enfrentado a una mano
 * sí — el producto solo, contra el gesto de usarlo.
 */
const ICONO_ACCION: Record<AccionVideoId, LucideIcon> = {
  presenta: SprayCan,
  aplica: Hand,
};

/** Ícono de cada ambiente, hasta que lleguen las fotos (ver `AMBIENTES`). */
const ICONO_AMBIENTE: Record<AmbienteId, LucideIcon> = {
  bano: Bath,
  tocador: Sparkles,
  spa: Leaf,
  // `Aperture` y no una claqueta: el estudio es fotográfico —fondo liso y luz
  // controlada—, y la claqueta lee como "rodaje de cine".
  estudio: Aperture,
  exterior: Sun,
};

/** El tilde de la tarjeta elegida en las grillas del panel lateral. */
const Tilde: React.FC = () => (
  <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white grid place-items-center shadow-md ring-2 ring-white">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  </span>
);

type PanelLateral = 'imagen' | 'ambiente' | 'modelo' | null;

/**
 * Quién usa el producto: la MISMA `ModelSelection` que el modo Modelo
 * (ADR-2026-09-10-el-picker-de-modelo-es-el-mismo-que-el-del-modo-modelo). Arranca en
 * `auto`, que es "la elige Atlas".
 *
 * Sólo aplica a la acción `aplica` ("Una modelo usándolo"), que es la única con una
 * persona en cuadro: en "Solo el producto" no hay nadie a quien elegir
 * (ADR-2026-09-10-la-modelo-se-elige-con-fotos-y-el-video-solo-lee-texto).
 */
const MODELO_AUTO: ModelSelection = { mode: 'auto' };

export default function MostrarComoFuncionaBellezaMode() {
  const navigate = useNavigate();
  const {
    sidebarWidth,
    setMobileBackOverride,
    setMobileHideBottomNav,
    setMobileHeaderTransparent,
  } = useSidebar();
  const volverAtras = useVolverAtras();
  const { rubro, marcaId, loaded: rubroCargado } = useRubroMarcaActiva();
  // Cómo se llama en pantalla la persona del video para este rubro
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = palabraDeModelo(rubro);

  // ── Gate de rubro ─────────────────────────────────────────────────────────
  // La card no se muestra fuera de Belleza y Cuidado Personal (`soloBelleza`), pero la
  // ruta existe igual: alguien con el link, o una cuenta que cambió de marca con la
  // pantalla abierta. Con el rubro resuelto y sin ser el correcto, a la home. Mientras
  // NO está resuelto no se rebota: el hook arranca en `loaded: false` y echar a alguien
  // que sí califica es peor que un instante de pantalla vacía.
  useEffect(() => {
    if (rubroCargado && !esRubroBelleza(rubro)) {
      navigate('/interfaz-v2', { replace: true });
    }
  }, [rubroCargado, rubro, navigate]);

  // ── Cuenta ────────────────────────────────────────────────────────────────
  // Arranca de la empresa ya resuelta en esta pestaña y no en `null`, para que la
  // columna de resultados no dibuje esqueletos medio segundo
  // (ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa).
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      setUserId(auth.user.id);
      // Sólo la empresa: la MARCA activa la resuelve `useRubroMarcaActiva`, con el
      // desempate de `current_brand_id` → `is_default` → la primera. Leerla acá a mano
      // devolvía `null` en las cuentas que nunca cambiaron de marca, y con eso la grilla
      // del paso 1 no tenía por qué filtrar
      // (ADR-2026-09-10-las-creaciones-del-paso-1-son-de-la-marca-activa).
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const perfil = p as { id_company?: string | null } | null;
      setCompanyId(perfil?.id_company ?? null);
      recordarEmpresa(perfil?.id_company, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  /**
   * Las modelos, una sola vez por empresa. `fetchUserModels` devuelve las globales de
   * Atlas (`id_company` null) MÁS las privadas de la cuenta, con los thumbnails ya
   * firmados — es la misma lista que ve el modo Modelo, así que "mis modelos" significa
   * lo mismo en los dos lugares.
   */
  const recargarModelos = React.useCallback(() => {
    if (!companyId) return;
    fetchUserModels(companyId).then(setModelos);
  }, [companyId]);

  useEffect(() => {
    recargarModelos();
  }, [recargarModelos]);

  // ── Lo que se elige ───────────────────────────────────────────────────────
  const [paso, setPaso] = useState<'elegir' | 'configurar'>('elegir');
  const [source, setSource] = useState<AnimarSource | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Arranca en "Una modelo usándolo": es la promesa del modo —ver el producto EN USO— y
  // el camino que más se elige; "Solo el producto" es la variante para cuando la marca no
  // quiere a nadie en cuadro
  // (ADR-2026-09-10-que-pasa-en-el-video-una-modelo-o-el-producto-solo).
  const [accion, setAccion] = useState<AccionVideoId>('aplica');
  /** Videos de ejemplo cuyo archivo no está: la muestra cae al del rubro. */
  const [videosRotos, setVideosRotos] = useState<string[]>([]);
  const [ambienteId, setAmbienteId] = useState<AmbienteElegido>('bano');
  /**
   * El ambiente escrito a mano, cuando está elegido "Lo describo yo". Se guarda aparte
   * de `ambienteId` para que volver a un ambiente curado y arrepentirse no lo borre.
   */
  const [ambientePropio, setAmbientePropio] = useState('');
  const [duracion, setDuracion] = useState(DURACION_DEFAULT);
  const [instruccion, setInstruccion] = useState('');
  const [modelo, setModelo] = useState<ModelSelection>(MODELO_AUTO);
  /** Las modelos que la cuenta puede usar: las globales de Atlas + las suyas. */
  const [modelos, setModelos] = useState<ModelV2[]>([]);
  /**
   * La sub-vista del picker: `list` es la grilla; `create` y `upload` son los formularios
   * de "Creá tu modelo" y "Cargá tu modelo", que el picker dibuja en su lugar. La
   * controla el padre —así lo pide el componente— y se resetea al cerrar el panel, para
   * que reabrirlo no caiga en un formulario a medio llenar.
   */
  const [modelView, setModelView] = useState<ModelPickerView>('list');

  // ── Capas ─────────────────────────────────────────────────────────────────
  const [panelLateral, setPanelLateral] = useState<PanelLateral>(null);
  /** Mobile: los dos pickers viven en bottom sheets en vez del panel lateral. */
  const [sheetImagen, setSheetImagen] = useState(false);
  const [sheetAmbiente, setSheetAmbiente] = useState(false);
  const [sheetModelo, setSheetModelo] = useState(false);

  // ── Estado de la generación ───────────────────────────────────────────────
  const [subiendo, setSubiendo] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const pendingNavRef = useRef<{ videoId: string; companyId: string } | null>(null);
  /** El plan que devolvió el último guard: lo necesita el modal cuando quien avisa
   *  del límite es el backend, ya con la tanda en la columna. */
  const planRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Cada selección invalida las subidas anteriores en vuelo. */
  const uploadSeqRef = useRef(0);

  const costo = costoVideo(duracion);
  const esAmbientePropio = ambienteId === AMBIENTE_PROPIO_ID;
  /**
   * La modelo se elige sólo en "Una modelo usándolo": es la única acción con una persona
   * en cuadro. En "Solo el producto" no hay nadie, así que la card no se dibuja.
   *
   * Quién contesta es el dominio (`accionTieneModelo`) y no un `accion === 'aplica'`
   * acá: la misma respuesta decide si la descripción de la modelo entra al prompt, y las
   * dos tienen que moverse juntas.
   */
  const usaModelo = accionTieneModelo(accion);

  /**
   * Lo ÚNICO de la modelo que puede cruzar al video, porque el prompt es texto.
   *
   * Con una modelo PERSONALIZADA ("Creá tu modelo") son sus rasgos, y eso cambia el
   * video de verdad. Con una GUARDADA es su `descripcion` de la base, que hoy está vacía
   * en todas — o sea que en la práctica no viaja nada, y por eso el panel lo avisa
   * (ADR-2026-09-10-el-picker-de-modelo-es-el-mismo-que-el-del-modo-modelo).
   */
  const descripcionModelo: string | null = (() => {
    if (modelo.mode === 'custom') return descripcionModeloEnPalabras(modelo.fields);
    if (modelo.mode === 'preset') {
      const m = modelos.find((u) => String(u.id) === modelo.id);
      return m?.descripcion ?? null;
    }
    return null;
  })();
  const ambiente = ambientePorId(ambienteId as AmbienteId);

  const nombreModo = nombreCortoModo(PATH_MODO, 'Mostrar cómo funciona');

  // ── Back flotante de mobile ───────────────────────────────────────────────
  // Además de dar el botón, esconde la bottom nav (`MobileBottomNav` se apaga con
  // `mobileBackOverride !== null`), que es lo que hacen el resto de los modos.
  /**
   * Volver al paso 1 NO borra la imagen elegida.
   *
   * Antes limpiaba `source` y `previewUrl`, así que ir para atrás a mirar la grilla
   * costaba la elección: la card volvía al ícono genérico y el "Siguiente" desaparecía.
   * Volver es navegar, no descartar — el que quiere otra imagen la toca en la grilla, y
   * el que sólo fue a mirar sigue con la suya
   * (ADR-2026-09-10-el-paso-1-de-mobile-recuerda-lo-elegido).
   */
  const volverAElegir = () => {
    setPaso('elegir');
    setError(null);
  };

  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'configurar') volverAElegir();
      // El back de la PRIMERA pantalla vuelve a donde estabas —
      // ADR-2026-09-07-el-back-de-los-modos-vuelve-a-donde-estabas.
      else volverAtras();
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, volverAtras]);

  /**
   * La barra inferior se aparta mientras el modo está abierto —
   * ADR-2026-08-05-la-barra-no-va-en-las-pantallas-tarea.
   *
   * Es una pantalla-TAREA a pantalla completa (y en el paso 2 trae su propia barra de
   * acciones abajo, con el Generar), no una sección entre las que se navega con tabs.
   *
   * Va por el flag y no metiendo la ruta en `RUTAS_SHELL_LEGACY`: esa lista es la de las
   * pantallas que NO nacieron para el shell nuevo, y ésta sí —monta `ShellModoRedisenado`—.
   * Los tres modos con ruta propia que sí están en esa lista (Animar, Carrusel, Shorts)
   * quedan sin barra de casualidad: les toca la barra VIEJA, que se esconde sola con
   * `mobileBackOverride`. La nueva no lo mira.
   */
  useEffect(() => {
    setMobileHideBottomNav(true);
    return () => setMobileHideBottomNav(false);
  }, [setMobileHideBottomNav]);

  /**
   * La pastilla de créditos del header de mobile, arriba a la derecha. El chrome sólo la
   * dibuja con el header transparente (`Sidebar`), que es lo que prenden la home y las
   * pantallas del preset sheet. Acá vale lo mismo que en Replicar una foto: el modo tapa
   * la pantalla y lo que se está por hacer cuesta créditos, así que el saldo tiene que
   * verse antes de llegar al Generar
   * (ADR-2026-09-10-el-paso-1-de-mobile-muestra-catalogo-y-saldo).
   */
  useEffect(() => {
    setMobileHeaderTransparent(true);
    return () => setMobileHeaderTransparent(false);
  }, [setMobileHeaderTransparent]);

  // ── La columna de resultados ──────────────────────────────────────────────
  const { columna, hayColumna, visorAbierto, lanzarTanda, puedeCrear } =
    useColumnaResultadosModo({
      companyId,
      brandId: marcaId,
      modo: KIND_MODO,
      onError: setError,
      onLimitePlan: () =>
        setLimitModal(
          planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        ),
    });

  // ── Elegir la imagen de partida ───────────────────────────────────────────
  const cerrarPickersDeImagen = () => {
    setPanelLateral(null);
    setSheetImagen(false);
  };

  /**
   * Elegir una creación. `avanzar` separa los dos lugares desde donde se elige:
   *
   * - En el PASO 1 (desktop) NO avanza: tocar una foto la deja marcada y el paso lo
   *   cierra el botón "Siguiente" del pie. Antes el primer toque saltaba de pantalla,
   *   así que no había forma de mirar la grilla, cambiar de idea o confirmar — y el
   *   resto de los modos (Replicar una foto, Automática, Modelo) piden ese "Siguiente".
   * - Desde el panel lateral y el sheet del paso 2 sí se aplica al toque: ahí el paso ya
   *   está abierto y lo único que se hace es cambiar la foto.
   */
  const elegirGenerada = (pick: GeneradaPick, avanzar: boolean) => {
    setSource({ tipo: 'generada', imageId: pick.id });
    setPreviewUrl(pick.signedUrl);
    cerrarPickersDeImagen();
    if (avanzar) setPaso('configurar');
  };

  /**
   * La foto propia SÍ se sube a storage —a diferencia del modo original, que la dejaba
   * en un `objectURL` local porque no generaba nada—: acá el backend tiene que poder
   * leerla, y `subirImagenParaAnimar` es exactamente el camino que ya usa Animar imagen
   * (bucket propio, sin registrar la foto como creación).
   */
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setError(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId);
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      setSource({ tipo: 'subida', bucket: subida.bucket, path: subida.path });
      setPreviewUrl(subida.previewUrl);
      setPanelLateral(null);
      setPaso('configurar');
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      if (seq === uploadSeqRef.current) {
        setSubiendo(false);
        // El sheet de mobile se cierra al TERMINAR la subida, con o sin éxito
        // (ADR-2026-09-08-la-subida-de-mobile-cierra-el-sheet): quien la dispara está
        // adentro del sheet, así que sin esto la pantalla de atrás avanza sola y el
        // usuario sigue mirando la misma lista.
        setSheetImagen(false);
      }
    }
  };

  // ── Generar ───────────────────────────────────────────────────────────────
  /** Lo que falta para poder generar, o `null`. Se muestra debajo del botón. */
  const falta: string | null = !source
    ? 'Elegí la imagen de partida.'
    : esAmbientePropio && !ambientePropio.trim()
      ? 'Contá dónde pasa el video.'
      : null;

  /**
   * El bloque adonde manda el Generar cuando falta algo — mismo orden que `falta`.
   *
   * El id lleva SUFIJO DE VIEWPORT: mobile y desktop montan los dos el bloque de
   * configuración a la vez (el de mobile es `md:hidden`, no está desmontado) y
   * `getElementById` se quedaría con el primero del documento —el oculto—, dejando el
   * scroll y la sacudida en la nada.
   *
   * Las dos ramas tienen target en los dos viewports desde que mobile también monta la
   * card de la imagen. Igual la de la imagen no se alcanza: sin imagen elegida el paso 2
   * —y con él este pie— no se monta.
   */
  const faltaTargetId = (sufijo: Viewport) =>
    !source
      ? `mostrar-como-funciona-imagen-${sufijo}`
      : `mostrar-como-funciona-ambiente-${sufijo}`;

  const generar = async () => {
    if (!source || !userId || !companyId || generando || falta) return;
    /**
     * En DESKTOP la tanda va a la columna de la derecha y la pantalla no se mueve; abajo
     * de `md` no hay dos columnas (el shell es `hidden md:flex`), así que ahí sigue el
     * camino de siempre: pantalla de carga y el video en el historial. Es la misma
     * bifurcación de Animar imagen.
     */
    const enPanel =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    setGenerando(true);
    setError(null);
    if (!enPanel) {
      setShowLoading(true);
      setCompleteNow(false);
    }
    try {
      // Guards con el dato fresco de Supabase: el piso de plan (el mismo que Animar
      // imagen, porque es el mismo pipeline) y saldo suficiente para la duración.
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan')
        .eq('id_company', companyId)
        .single();
      const plan = (perfil as { plan?: string } | null)?.plan;
      planRef.current = plan ?? null;
      if (plan && !isProOrUltra(plan)) {
        setShowLoading(false);
        setGenerando(false);
        setError(
          'Mostrar cómo funciona está disponible para los planes Pro y Ultra.',
        );
        return;
      }
      const restantes = await leerSaldo(companyId).then((s) => s.disponibles).catch(() => undefined);
      if (typeof restantes === 'number' && restantes < costo) {
        setShowLoading(false);
        setGenerando(false);
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }

      const body = buildAnimarVideoBody(source, {
        userId,
        // El prompt lo arma el dominio a partir de las decisiones de la columna: acá NO
        // se escribe texto de prompt (ver `mostrarComoFuncionaBelleza.ts`).
        prompt: promptVideoBelleza({
          accion,
          ambiente: ambienteId,
          ambientePropio,
          instruccion,
          // Lo ÚNICO de la modelo que puede cruzar al video, porque el prompt es texto.
          // Hoy `modelos.descripcion` está vacía en todas, así que en la práctica no
          // viaja nada — ver el ADR.
          modeloDescripcion: usaModelo ? descripcionModelo : null,
        }),
        duracion,
        // Una sola escena: el video es de 5-8 s y el multi-escena de Kling cobra el
        // doble y tarda 3-8 minutos. Si mañana hace falta, es un booleano.
        multiplesEscenas: false,
      });

      // La modelo elegida, con los MISMOS nombres de campo que ya usa el backend en el
      // endpoint de imágenes (`modelo_guardado_id` / `modelo_guardado_nombre`, ver
      // `InterfazV2.buildModeloConfigItem`). `/v1/videos/from-image` todavía NO los lee
      // —hoy la elección no cambia el video—, pero cuando los soporte van a llamarse así
      // y no hay que cambiar la pantalla
      // (ADR-2026-09-10-la-modelo-se-elige-con-fotos-y-el-video-solo-lee-texto).
      //
      // Se agregan DESPUÉS de `buildAnimarVideoBody` y no adentro: esa función la
      // comparte "Animar imagen", y sumarle campos ahí se los mandaría también a un modo
      // que no tiene modelo.
      if (usaModelo && modelo.mode === 'preset') {
        body.modelo_guardado_id = modelo.id;
        body.modelo_guardado_nombre = modelo.name;
      }

      if (enPanel) {
        // El tope se pregunta ANTES de disparar: acá todavía se puede frenar y decirlo
        // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
        if (!puedeCrear()) {
          setGenerando(false);
          setError(
            `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso. Esperá a que termine alguna para pedir otra.`,
          );
          return;
        }
        // La promesa se entrega SIN esperarla: los recuadros salen ya y el usuario se
        // queda en el modo, listo para pedir el próximo video.
        lanzarTanda({
          total: 1,
          esVideo: true,
          // Lo que cobra de verdad: en un video son los segundos, no "1 resultado".
          creditos: costo,
          resultado: generateVideoFromImage(body, { companyId }).then((id) => [id]),
          errorGenerico: 'No se pudo generar el video. Intentá nuevamente.',
        });
        setGenerando(false);
        return;
      }

      const videoId = await generateVideoFromImage(body, { companyId });
      // La fila del video puede tardar en tener `file_name`: esperamos un poco antes de
      // mandar al historial, igual que Animar imagen.
      for (let intento = 1; intento <= POLL_INTENTOS; intento++) {
        const { data: fila } = await supabase
          .from('imagenes_generadas')
          .select('file_name')
          .eq('id', videoId)
          .eq('id_company', companyId)
          .single();
        if (fila?.file_name) break;
        await new Promise((r) => setTimeout(r, POLL_INTERVALO_MS));
      }
      pendingNavRef.current = { videoId, companyId };
      setCompleteNow(true); // cierra la barra de carga → onCompleted navega
    } catch (e) {
      console.error('[MostrarComoFuncionaBellezaMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'No se pudo generar el video. Intentá nuevamente.',
      );
    }
  };

  // ── Piezas compartidas ────────────────────────────────────────────────────

  /**
   * El video de ejemplo de la muestra: el de la ACCIÓN elegida, no uno fijo
   * (ADR-2026-09-10-la-muestra-ensena-la-accion-elegida). Cambiar "Qué pasa en el video"
   * cambia lo que se ve a la derecha, que es la única forma de saber qué significa cada
   * tile antes de gastar créditos.
   *
   * `key={src}`: sin él, React reusa el mismo `<video>` al cambiar el `src` y el
   * navegador se queda con el frame anterior hasta que baje el nuevo.
   *
   * Si el archivo de la acción todavía no está en `public/` —hoy le falta el de "Solo el
   * producto"—, el `onError` lo anota y la muestra cae al video del rubro en vez de
   * dejar un recuadro negro.
   */
  const accionMuestra = accionVideoPorId(accion);

  const videoDeAccion = (a: AccionVideo, className: string, oculto = false) => {
    const roto = videosRotos.includes(a.videoUrl);
    return (
      <video
        key={a.id}
        src={roto ? HERO_VIDEO : a.videoUrl}
        poster={roto ? HERO_POSTER : a.posterUrl}
        onError={() =>
          setVideosRotos((prev) =>
            prev.includes(a.videoUrl) ? prev : [...prev, a.videoUrl],
          )
        }
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden={oculto}
        aria-label={`Ejemplo de "${a.nombre}" — modo ${nombreModo}`}
        className={className}
      />
    );
  };

  /** El video de la acción elegida, solo. Lo usa la portada del paso 1 de mobile. */
  const heroVideo = (className: string) => videoDeAccion(accionMuestra, className);

  /**
   * El mismo video, pero CRUZÁNDOSE con el de la otra acción.
   *
   * Los dos `<video>` viven montados en la MISMA celda de grilla (`col-start-1 row-start-1`) y lo
   * único que cambia es la opacidad: uno se va mientras el otro llega. Con un solo
   * elemento al que se le cambia el `src` el salto era seco —el navegador tira el frame
   * viejo y deja el hueco hasta que baja el nuevo—, y montarlos apilados en `absolute`
   * habría dejado al contenedor sin alto propio. La celda compartida además fija el alto
   * en el más alto de los dos, así que el bloque no salta al cambiar de opción.
   */
  const heroVideoCruzado = (className: string) => (
    <div className="grid place-items-center">
      {ACCIONES_VIDEO.map((a) =>
        videoDeAccion(
          a,
          `${className} col-start-1 row-start-1 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
            a.id === accionMuestra.id ? 'opacity-100' : 'opacity-0'
          }`,
          a.id !== accionMuestra.id,
        ),
      )}
    </div>
  );

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  /**
   * "Subir imagen" como PRIMERA CELDA de la grilla, no como botón arriba de ella — el
   * mismo lugar que ocupa "Subir nuevo producto" en la grilla de productos de los otros
   * modos, y por el mismo motivo: subir y elegir una creación son dos formas de
   * responder la MISMA pregunta.
   */
  const celdaSubirImagen = (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={subiendo}
      className="relative aspect-square rounded-[13px] overflow-hidden bg-white border-2 border-dashed border-neutral-300 hover:border-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.03)] disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-all flex flex-col items-center justify-center gap-2 px-2"
    >
      <span className="w-9 h-9 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.1)] flex items-center justify-center">
        {subiendo ? (
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
        ) : (
          <ImageUp className="w-5 h-5 text-brand-primary" />
        )}
      </span>
      <span className="text-[11.5px] font-bold text-brand-primary text-center leading-tight">
        {subiendo ? 'Subiendo…' : 'Subir imagen'}
      </span>
    </button>
  );

  /**
   * La grilla del modo: subir + las creaciones. La MISMA en los tres lugares donde se
   * elige la imagen —el paso 1, el panel lateral del paso 2 y el sheet de mobile—, que
   * es lo que hace que cambiarla sea el mismo gesto que elegirla.
   */
  const grillaDeImagenes = (avanzarAlElegir: boolean) => (
    <GeneradasPickGrid
      companyId={companyId}
      selectedId={source?.tipo === 'generada' ? source.imageId : null}
      onSelect={(pick) => elegirGenerada(pick, avanzarAlElegir)}
      // Sólo las creaciones de la MARCA activa, no las de toda la empresa: una cuenta
      // con varias marcas mezclaba rubros, y este modo pone en cámara el producto de
      // ESTA marca (ADR-2026-09-10-las-creaciones-del-paso-1-son-de-la-marca-activa).
      // `null` mientras el hook resuelve → la grilla espera en su esqueleto.
      brandId={marcaId}
      // Con el flag de rollback a n8n no hay forma de animar una foto suelta: ahí la
      // celda de subir no va y queda sólo el camino de las creaciones.
      tileInicial={SUBIDA_REQUIERE_ATLAS ? undefined : celdaSubirImagen}
      emptyHint="Todavía no hay imágenes generadas para esta marca. Subí una foto de tu producto."
    />
  );

  /**
   * "Qué pasa en el video": DOS tiles, y lo que las separa es si hay ALGUIEN en cuadro.
   * La bajada lo dice de entrada porque es justo la confusión que había cuando el tile
   * del producto se llamaba "Mostrando el producto": se leía como "una modelo lo
   * muestra", o sea lo mismo que el otro.
   */
  const bloqueAccion = (
    <div className="shrink-0 rounded-2xl bg-black/[0.035] p-3 flex flex-col gap-2.5">
      <div className="px-1">
        <p className="text-[11px] font-bold uppercase tracking-wide text-black/45 leading-none">
          Qué pasa en el video
        </p>
        <p className="text-[12px] text-black/45 leading-snug mt-1.5">
          Con un {palabra.singular} en cuadro, o el producto solo.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {ACCIONES_VIDEO.map((a) => {
          const activa = a.id === accion;
          const Icono = ICONO_ACCION[a.id];
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setAccion(a.id)}
              aria-pressed={activa}
              className={`flex flex-col items-center gap-2 px-2 py-3 rounded-xl bg-white transition-shadow ${
                activa
                  ? 'ring-2 ring-brand-primary'
                  : 'ring-1 ring-transparent hover:ring-black/[0.08]'
              }`}
            >
              <Icono
                className={`w-[26px] h-[26px] ${
                  activa ? 'text-brand-primary' : 'text-black/55'
                }`}
                strokeWidth={1.6}
              />
              <span
                className={`text-[12.5px] leading-tight text-center ${
                  activa ? 'font-semibold text-brand-black' : 'font-medium text-black/75'
                }`}
              >
                {a.nombre}
              </span>
            </button>
          );
        })}
      </div>
      <p className="px-1 text-[12.5px] leading-snug text-black/50">
        {accionVideoPorId(accion).descripcion}
      </p>
    </div>
  );

  /** El valor que muestra la card "Dónde pasa". */
  const valorAmbiente = esAmbientePropio
    ? ambientePropio.trim() || 'Escribí dónde pasa'
    : `${ambiente.nombre} · ${ambiente.descripcion}`;
  const IconoAmbiente = esAmbientePropio ? PencilLine : ICONO_AMBIENTE[ambiente.id];

  const cardAmbiente = (abrir: () => void, sufijo: Viewport) => (
    // El envoltorio existe sólo para colgarle el id: `CardPicker` no lo recibe, y es
    // este bloque —no el botón de adentro— el que se sacude cuando falta el ambiente.
    <div id={`mostrar-como-funciona-ambiente-${sufijo}`}>
      <CardPicker
        etiqueta="Dónde pasa"
        valor={valorAmbiente}
        imagenes={
          !esAmbientePropio && ambiente.imageUrl ? [ambiente.imageUrl] : []
        }
        vacio={<IconoAmbiente className="w-6 h-6 text-brand-black/25" />}
        onClick={abrir}
      />
    </div>
  );

  /**
   * El textarea de "Contá dónde pasa", con su historial de instrucciones. Se dibuja
   * DEBAJO de la grilla de ambientes —en el panel lateral y en el sheet—, porque el
   * picker es la decisión entera: escribir tiene que pasar ahí adentro.
   */
  const campoAmbientePropio = (
    <div className="mt-4">
      <label
        htmlFor="ambiente-propio-belleza"
        className="block text-[12.5px] font-semibold text-brand-black mb-1.5"
      >
        Contá dónde pasa
      </label>
      <div className="relative">
        <textarea
          id="ambiente-propio-belleza"
          autoFocus
          value={ambientePropio}
          onChange={(e) => setAmbientePropio(e.target.value)}
          placeholder="Un baño de azulejo verde con luz de atardecer entrando por la ventana"
          className="w-full min-h-[104px] rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
        />
        <PromptRecientesButton
          tipo="instruccion"
          companyId={companyId}
          onPick={setAmbientePropio}
          titulo="Instrucciones usadas antes"
          className="absolute bottom-2.5 right-2.5"
        />
      </div>
      <p className="mt-2 text-[12px] leading-snug text-black/45">
        El producto sale de tu foto: acá va lo de alrededor —el lugar, los objetos y la
        luz—.
      </p>
    </div>
  );

  /**
   * La grilla de ambientes. La comparten el panel lateral (desktop) y el sheet (mobile);
   * `cerrar` es lo único que cambia entre los dos.
   */
  const grillaAmbientes = (cerrar: () => void, columnas: number) => (
    <>
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}
      >
        {AMBIENTES.map((a) => {
          const activa = a.id === ambienteId;
          const Icono = ICONO_AMBIENTE[a.id];
          return (
            <button
              key={a.id}
              type="button"
              aria-pressed={activa}
              onClick={() => {
                setAmbienteId(a.id);
                cerrar();
              }}
              className={`relative flex flex-col text-left rounded-2xl overflow-hidden bg-neutral-50 transition-shadow ${
                activa
                  ? 'ring-2 ring-brand-primary'
                  : 'ring-1 ring-black/[0.06] hover:ring-black/[0.14]'
              }`}
            >
              <div className="relative aspect-[4/3] bg-neutral-100 grid place-items-center">
                {a.imageUrl ? (
                  <CachedImg
                    src={a.imageUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <Icono className="w-8 h-8 text-black/35" strokeWidth={1.5} />
                )}
                {activa && <Tilde />}
              </div>
              <div className="px-3 py-2.5">
                <p className="text-[13.5px] font-semibold text-brand-black leading-tight">
                  {a.nombre}
                </p>
                <p className="text-[12px] text-black/50 leading-snug mt-0.5">
                  {a.descripcion}
                </p>
              </div>
            </button>
          );
        })}

        {/* La opción extra: describirlo a mano. Misma tarjeta que las curadas —para que
            se lea como una opción más de la misma decisión y no como un escape—, pero
            punteada y en azul, que es como la app viene marcando "esto lo ponés vos".

            NO cierra el picker al elegirla: elegir un ambiente curado termina la
            decisión, pero acá recién empieza —hay que escribir—, y cerrar dejaría al
            usuario mirando una card que dice "Escribí dónde pasa" sin dónde hacerlo. */}
        <button
          type="button"
          aria-pressed={esAmbientePropio}
          onClick={() => setAmbienteId(AMBIENTE_PROPIO_ID)}
          className={`relative flex flex-col text-left rounded-2xl overflow-hidden transition-shadow ${
            esAmbientePropio
              ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.04)] ring-2 ring-brand-primary'
              : 'bg-[rgb(var(--brand-primary-rgb)_/_0.02)] ring-[1.5px] ring-dashed ring-[rgb(var(--brand-primary-rgb)_/_0.35)] hover:ring-[rgb(var(--brand-primary-rgb)_/_0.6)]'
          }`}
        >
          <div className="relative aspect-[4/3] grid place-items-center">
            <span className="w-10 h-10 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] grid place-items-center text-brand-primary">
              <PencilLine className="w-[18px] h-[18px]" />
            </span>
            {esAmbientePropio && <Tilde />}
          </div>
          <div className="px-3 py-2.5">
            <p className="text-[13.5px] font-semibold text-brand-primary leading-tight">
              {AMBIENTE_PROPIO.nombre}
            </p>
            <p className="text-[12px] text-black/50 leading-snug mt-0.5">
              {AMBIENTE_PROPIO.descripcion}
            </p>
          </div>
        </button>
      </div>
      {esAmbientePropio && campoAmbientePropio}
    </>
  );

  /** La miniatura de lo elegido, para la card — la misma que usa el modo Modelo. */
  const fotoModelo = (() => {
    if (modelo.mode === 'preset') {
      const m = modelos.find((u) => String(u.id) === modelo.id);
      return m?.thumbUrl || m?.thumbnailUrl || modelo.imageUrl || null;
    }
    if (modelo.mode === 'upload') return modelo.previewUrl;
    return null;
  })();

  const cardModelo = (abrir: () => void) => (
    <CardPicker
      etiqueta={palabra.titulo}
      // `modelSelectionLabel` es la misma función que rotula la card del modo Modelo:
      // "Automático", el nombre de la guardada, o "Mujer · Joven" en una personalizada.
      valor={modelSelectionLabel(modelo)}
      imagenes={fotoModelo ? [fotoModelo] : []}
      vacio={<Sparkles className="w-6 h-6 text-brand-black/25" />}
      onClick={abrir}
    />
  );

  /**
   * El picker de modelo: EL MISMO componente que el modo Modelo
   * (ADR-2026-09-10-el-picker-de-modelo-es-el-mismo-que-el-del-modo-modelo).
   *
   * Trae la tarjeta "Automático", "Creá tu modelo" y "Cargá tu modelo", la grilla de las
   * guardadas —globales de Atlas y las de la cuenta— y los dos formularios completos,
   * que se dibujan en el lugar de la grilla al tocarlos. Acá se escribía una grilla
   * propia con la mitad de eso.
   *
   * NO se le pasan `onChangeExtras` ni `onMultiEnabledChange`: sin esos handlers el
   * picker no dibuja el "Utilizar varios modelos", que es lo correcto — en un video de
   * 6 s de una persona aplicándose un producto, varias modelos no entran.
   */
  /**
   * El picker de modelo. `alElegir` corre DESPUÉS de aplicar la elección: en mobile lo
   * usa el sheet para cerrarse solo, que es como se comportan los otros dos sheets del
   * modo (imagen y ambiente) y el `ModelPicker` de pantalla completa. Sin eso había que
   * elegir la modelo y después cerrar el sheet a mano, y el tap parecía no haber hecho
   * nada. El panel lateral de desktop no lo pasa: ahí la grilla queda a la vista y el
   * anillo del tile ya dice qué está elegido.
   */
  const contenidoPickerModelo = (columnas: number, alElegir?: () => void) => (
    <>
      {/* La bajada, sólo sobre la GRILLA. Con `create` o `upload` el picker reemplaza su
          contenido por un formulario que tiene su propio encabezado, y dejarla arriba
          explicaría algo que en esa vista ya no se está haciendo. */}
      {modelView === 'list' && (
        <>
          {/* `mb-4` y no `mb-3`: los 16px hasta la grilla los ponía el aviso que estaba
              abajo de esta línea; al sacarlo, el margen tiene que pasar acá o la grilla
              sube 4px. */}
          <p className="text-[13px] text-[#3C3C43]/60 leading-relaxed mb-4">
            Quién usa el producto en el video.
          </p>
          {/* ACÁ IBA el aviso "Todavía en camino" —
              ADR-2026-09-10-el-aviso-de-la-modelo-guardada-se-esconde.
              Se sacó a pedido, y es una decisión de producto, no un arreglo: LA
              LIMITACIÓN SIGUE. El endpoint de video (`/v1/videos/from-image`) recibe
              una foto de producto y un prompt, y nada más, así que una modelo GUARDADA
              —que es una foto— no cruza: queda registrada en la elección pero el video
              la sigue poniendo Atlas. Una descrita con "Creá tu modelo" sí llega, porque
              son palabras y viajan en el prompt.
              O sea que desde ahora el usuario elige una cara y recibe otra sin nada que
              se lo diga. Cuando el motor acepte la foto de la modelo esto deja de
              importar; hasta entonces, el cartel es el candidato obvio a volver. */}
        </>
      )}
      <ModelPickerInline
        selection={modelo}
        onChange={(sel) => {
          setModelo(sel);
          alElegir?.();
        }}
        view={modelView}
        onViewChange={setModelView}
        userModels={modelos}
        userId={userId}
        companyId={companyId}
        onModelCreated={recargarModelos}
        // El título lo pone la cabecera del panel; el picker no lo repite.
        hideLabel
        // Grilla que scrollea, no el carrusel horizontal: es lo que usa el panel lateral
        // del modo Modelo.
        grilla
        columnasGrilla={columnas}
      />
    </>
  );

  /**
   * La instrucción libre, ÚLTIMA y a todo el alto que sobre.
   *
   * Va al final, como en Modelo y en Automático: primero se decide, y lo último es
   * afinar con palabras. El `flex-1` le da el sobrante y el `min-h` la sostiene cuando
   * no sobra nada (ADR-2026-09-03-el-paso-2-cierra-con-la-instruccion).
   */
  const bloqueInstruccion = (
    <div className="relative flex-1 min-h-[112px] flex flex-col">
      <textarea
        value={instruccion}
        onChange={(e) => setInstruccion(e.target.value)}
        placeholder="Agregá indicaciones extras para afinar el resultado"
        className="flex-1 w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black placeholder:text-black/35 resize-none transition-colors"
      />
      {/* El mismo historial de instrucciones que el resto de los modos
          (ADR-2026-08-12-historial-instrucciones-reusables). El `companyId` va
          explícito: esta pantalla vive afuera del `AgentEnvContext` de `InterfazV2`. */}
      <PromptRecientesButton
        tipo="instruccion"
        companyId={companyId}
        onPick={setInstruccion}
        titulo="Instrucciones usadas antes"
        className="absolute bottom-2.5 right-2.5"
      />
    </div>
  );

  /**
   * La duración, como CELDA del pie (ADR-2026-09-04-la-duracion-es-una-celda-del-pie).
   *
   * Va SOLA en su fila: el modo original la acompañaba con la celda de Formato, que acá
   * no existe —el backend fija 9:16 y ofrecer un selector que no se respeta es mentir—.
   */
  const celdaDuracion = (
    <CeldaAjuste etiqueta="Duración" solo>
      <FillBarSliderV2
        label="Duración"
        variante="celda"
        min={DURACION_MIN}
        max={DURACION_MAX}
        step={1}
        value={duracion}
        onChange={setDuracion}
        formatValue={(v) => `${v} s`}
      />
    </CeldaAjuste>
  );

  /**
   * El Generar. Con algo sin elegir NO se apaga: se atenúa y, al tocarlo, scrollea hasta
   * el bloque que falta y lo sacude — el mismo gesto que el Generar del modo Video con
   * influencer (ADR-552 / ADR-553). Un botón muerto obliga a adivinar cuál de los
   * ajustes es el que le falta; éste lo señala.
   *
   * Por eso `aria-disabled` en vez de `disabled`: apagado de verdad no recibiría el
   * click. `disabled` queda sólo para mientras genera, que ahí sí no hay nada que decir.
   */
  const botonGenerar = (sufijo: Viewport) => (
    <button
      type="button"
      onClick={() => {
        if (falta) {
          scrollToAndShake(faltaTargetId(sufijo));
          return;
        }
        void generar();
      }}
      aria-disabled={!!falta}
      disabled={generando}
      className={`w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors ${
        falta ? 'opacity-40' : ''
      }`}
    >
      <Sparkles className="w-5 h-5" />
      Generar video
      <CreditBadge credits={costo} tone="on-primary" size="sm" />
    </button>
  );

  /** El bloque de configuración: lo comparten el panel de desktop y el de mobile. Lo
   *  único que cambia entre los dos es CÓMO se abre el picker de ambiente. */
  /**
   * La card de la imagen de partida: QUÉ foto se va a animar, y el camino para cambiarla.
   *
   * La comparten los dos viewports. En desktop encabeza el panel, fuera del scroll; en
   * mobile va DENTRO de la configuración, arriba de todo: desde que el paso 2 es
   * pantalla completa (ADR-2026-09-10-el-paso-1-de-mobile-recuerda-lo-elegido) la foto
   * ya no se ve de fondo, así que sin esta card no había forma de saber cuál está
   * elegida ni de cambiarla sin volver al paso 1.
   */
  const cardImagen = (abrir: () => void, sufijo: Viewport) => (
    <div id={`mostrar-como-funciona-imagen-${sufijo}`}>
      <CardPicker
        etiqueta="Imagen de partida"
        valor={source?.tipo === 'subida' ? 'Foto que subiste' : 'Una creación tuya'}
        imagenes={previewUrl ? [previewUrl] : []}
        vacio={<ImageUp className="w-6 h-6 text-brand-black/25" />}
        onClick={abrir}
      />
    </div>
  );

  const bloqueConfigurar = (
    abrirAmbiente: () => void,
    abrirModelo: () => void,
    sufijo: Viewport,
  ) => (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      {cardAmbiente(abrirAmbiente, sufijo)}
      {bloqueAccion}
      {/* La modelo va PEGADA al bloque de acción y no arriba con las otras cards:
          aparece y desaparece con el tile elegido, así que tiene que leerse como parte
          de esa decisión y no como una card fija que a veces falta. */}
      {usaModelo && cardModelo(abrirModelo)}
      {celdaDuracion}
      {bloqueInstruccion}
      {errorBox}
    </div>
  );

  /** El pie con el CTA y, debajo, lo que falta — que no mueve el botón de lugar. */
  const pieGenerar = (clases: string, sufijo: Viewport) => (
    <div className={clases}>
      {botonGenerar(sufijo)}
      {falta && !generando && (
        <p className="mt-2 text-[13px] text-black/50 px-1">{falta}</p>
      )}
    </div>
  );

  // ── DESKTOP: lo que va adentro del shell ──────────────────────────────────

  const panelElegir = (
    <>
      <div className="shrink-0 px-6 flex justify-between gap-2 items-baseline pt-5 pb-1">
        <p className="text-[17px] font-semibold text-brand-black leading-tight">
          Elegí la foto de tu producto
        </p>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
        {grillaDeImagenes(false)}
        {errorBox}
      </div>
      {/* El pie con el "Siguiente": el paso 1 se cierra a mano, como en el resto de los
          modos. Vive fuera del scroll para que no haya que bajar la grilla entera para
          encontrarlo. */}
      <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]">
        <button
          type="button"
          onClick={() => setPaso('configurar')}
          disabled={!source}
          className="w-full inline-flex items-center justify-center py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          Siguiente
        </button>
      </div>
    </>
  );

  const panelConfigurar = (
    <>
      {/* La imagen elegida encabeza la configuración con la MISMA card con la que Fotos
          de producto y Catálogo encabezan la suya con los productos. Tocarla abre el
          panel lateral, sin salir de la configuración. */}
      <div className="shrink-0 px-6 pt-5 pb-1">
        {cardImagen(() => setPanelLateral('imagen'), 'desktop')}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-4 flex flex-col">
        {bloqueConfigurar(
          () => setPanelLateral('ambiente'),
          () => setPanelLateral('modelo'),
          'desktop',
        )}
      </div>
      {pieGenerar(
        'shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]',
        'desktop',
      )}
    </>
  );

  const enConfigurar = paso === 'configurar' && !!source && !!previewUrl;

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      {/* MOBILE: los dos pickers, en bottom sheets. */}
      <OptionSheet
        open={sheetImagen}
        onClose={() => setSheetImagen(false)}
        title="Elegí la imagen"
        hideAccept
      >
        {grillaDeImagenes(true)}
        {errorBox}
      </OptionSheet>
      <OptionSheet
        open={sheetAmbiente}
        onClose={() => setSheetAmbiente(false)}
        title="Dónde pasa"
        hideAccept
      >
        <p className="text-[13px] text-[#3C3C43]/60 mb-3">
          El lugar donde pasa el video. La luz y lo que hay alrededor salen de acá.
        </p>
        {grillaAmbientes(() => setSheetAmbiente(false), 2)}
      </OptionSheet>
      <OptionSheet
        open={sheetModelo}
        onClose={() => {
          setSheetModelo(false);
          setModelView('list');
        }}
        title={palabra.titulo}
        hideAccept
      >
        {contenidoPickerModelo(2, () => {
          setSheetModelo(false);
          setModelView('list');
        })}
      </OptionSheet>
      {showLoading && (
        <LoadingAnimation
          message="Generando video…"
          subMessage="Esto puede tardar unos minutos."
          durationMs={CARGA_MS}
          completeNow={completeNow}
          onCompleted={() => {
            setShowLoading(false);
            setGenerando(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: [pend.videoId], companyId: pend.companyId },
              });
            }
          }}
        />
      )}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );

  return (
    <>
      {/* ── MOBILE ────────────────────────────────────────────────────────── */}
      {!enConfigurar ? (
        // La anatomía con la que abren TODOS los modos en mobile
        // (ADR-2026-09-08-todos-los-modos-abren-igual-en-mobile). "Subir imagen" no se
        // pierde: vive como primera celda de la grilla del sheet.
        <PasoUnoMobile
          titulo="Elegí la foto de tu producto"
          bajada={`Atlas arma un video corto de tu producto: un ${palabra.singular} usándolo, o el producto solo.`}
          // El clip es 9:16: con el `max-w-[400px]` de los modos de portada apaisada
          // se estiraba a ~690px de alto y empujaba la card del pie fuera de la
          // pantalla. El tope va por ANCHO —240px—, que es lo que lo deja entrar por
          // alto en un teléfono.
          portada={heroVideo(
            'max-w-[240px] max-h-full w-full h-auto object-contain rounded-2xl',
          )}
          Icono={ImageUp}
          cardTexto="Elegí tu imagen"
          onAbrir={() => setSheetImagen(true)}
          // La imagen ya elegida, en la miniatura: volviendo del paso 2 la elección
          // sigue hecha, y una card con el ícono genérico hace dudar de si se perdió.
          miniaturas={
            previewUrl ? (
              // Los mismos 52px del recuadro del ícono, para que la card no cambie de
              // forma entre un estado y el otro.
              <span className="block h-[52px] w-[52px] shrink-0 overflow-hidden rounded-xl bg-white">
                <CachedImg
                  src={previewUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </span>
            ) : undefined
          }
          // Y la vuelta al paso 2 sin reabrir la grilla ni volver a tocar lo mismo.
          onSiguiente={
            source && previewUrl ? () => setPaso('configurar') : undefined
          }
          aviso={errorBox}
        />
      ) : (
        /* PASO 2: la configuración a PANTALLA COMPLETA, como Replicar una foto y como
           el `configure` de Modelo (ADR-2026-09-10-replicar-foto-mobile-el-paso-2-es-pantalla-completa).

           Antes era el dibujo de Animar imagen: la foto de partida a sangre arriba y la
           configuración en un panel de 78dvh. Con seis controles —ambiente, acción,
           modelo, duración, instrucción— el panel no daba: el flex se cobraba el
           faltante achicando las cards en vez de dejar scrollear, y la de Modelo
           quedaba aplastada. La foto de partida no se pierde: se ve en su card, que es
           además la que la cambia.

           `pt-16`: el chrome del shell dibuja el back y la pastilla de créditos
           flotando arriba. */
        <div className="md:hidden fixed inset-0 z-30 bg-white flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-16 pb-6 flex flex-col gap-4">
            {/* El nombre del modo, adentro de la pantalla: el header de mobile es
                transparente y sin título, así que sin esto el paso 2 no dice en qué modo
                estás. Va con el scroll; lo que queda fijo es el Generar del pie. */}
            <h1 className="shrink-0 text-[22px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
              {nombreModo}
            </h1>
            {cardImagen(() => setSheetImagen(true), 'mobile')}
            {bloqueConfigurar(
              () => setSheetAmbiente(true),
              () => setSheetModelo(true),
              'mobile',
            )}
          </div>
          {pieGenerar(
            'shrink-0 bg-white px-5 pt-3 pb-5 border-t border-black/[0.06]',
            'mobile',
          )}
        </div>
      )}

      {/* ── DESKTOP: el marco y las dos columnas los pone el shell compartido ── */}
      <ShellModoRedisenado
        sidebarWidth={sidebarWidth}
        // Dos niveles, como el resto de los modos: la home y el modo. El paso NO se suma
        // como tercer nivel — la cabecera del panel no cambia de paso
        // (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso); volver al paso 1 es
        // tocar la card de la imagen.
        migas={[
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreModo },
        ]}
        titulo={nombreModo}
        panel={enConfigurar ? panelConfigurar : panelElegir}
        panelLateral={({ contenedorRef, offsetIzquierdo }) => (
          <PanelLateralModo
            abierto={enConfigurar && panelLateral !== null}
            onCerrar={() => {
              setPanelLateral(null);
              // El picker de modelo vuelve a su grilla: reabrirlo no tiene que caer en
              // el formulario de "Creá tu modelo" a medio llenar.
              setModelView('list');
            }}
            titulo={
              panelLateral === 'ambiente'
                ? 'Dónde pasa'
                : panelLateral === 'modelo'
                  ? palabra.titulo
                  : 'Imagen de partida'
            }
            offsetIzquierdo={offsetIzquierdo}
            contenedorRef={contenedorRef}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
              {panelLateral === 'ambiente' ? (
                <>
                  <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                    El lugar donde pasa el video. La luz y lo que hay alrededor salen de
                    acá.
                  </p>
                  {grillaAmbientes(() => setPanelLateral(null), 3)}
                </>
              ) : panelLateral === 'modelo' ? (
                contenidoPickerModelo(3)
              ) : (
                <>
                  <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                    Subí una foto nueva o elegí otra de tus creaciones. El panel se cierra
                    al elegir.
                  </p>
                  {grillaDeImagenes(true)}
                  {errorBox}
                </>
              )}
            </div>
          </PanelLateralModo>
        )}
        // Con resultados en la columna, ésta se sale de los paddings y llega al marco
        // (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco).
        muestraAlBorde={hayColumna}
        // El visor de una creación es un lightbox portaleado al body: sin bajarle el z a
        // este panel queda debajo y sólo se ve su velo.
        cederZIndex={visorAbierto}
        muestra={
          columna ?? (
            <div
              key="muestra-video"
              className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 animate-paso-adelante"
            >
              {/* El texto ENCABEZA la muestra en vez de ir de pie de foto — el mismo
                  tratamiento de Modelo v2 y Catálogo
                  (ADR-2026-08-21-modelo-v2-la-muestra-se-queda).

                  Con el producto ya elegido, el encabezado pasa a ser la ACCIÓN: el
                  video de abajo es el ejemplo de lo que está tildado en "Qué pasa en el
                  video", así que lo que tiene que decir es cuál es. Antes acá se
                  repetía la foto de partida —que ya se ve en su card, a la izquierda— y
                  el ejemplo desaparecía justo cuando había que elegir entre las dos
                  acciones. */}
              {/* `key` + `animate-paso-titulo`: al cambiar de acción el texto se desvanece
                  y vuelve, en vez de reemplazarse de golpe. Es la misma animación que usa
                  el encabezado del panel cuando cambia de paso —sólo opacidad, sin
                  desplazamiento— porque acá el que se mueve es el video de abajo. */}
              <div
                key={enConfigurar ? accionMuestra.id : 'hero'}
                className="shrink-0 text-center max-w-[440px] mb-3 animate-paso-titulo"
              >
                <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">
                  {enConfigurar ? accionMuestra.nombre : HERO_TITULO}
                </h2>
                <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                  {enConfigurar ? accionMuestra.descripcion : HERO_DESC}
                </p>
              </div>
              {heroVideoCruzado(
                'max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl',
              )}
            </div>
          )
        }
      />

      {overlays}
    </>
  );
}
